import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import SyncIcon from '@mui/icons-material/Sync';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SettingsIcon from '@mui/icons-material/Settings';
import RuleIcon from '@mui/icons-material/Rule';
import { useNavigate } from 'react-router-dom';
import { useAuth, signIn, signUp, signOut } from '../auth/auth';
import { useSyncStatus } from '../sync/status';
import { isLinked, linkAccount, resumeSync, syncNow } from '../sync/controller';
import { useAppStore } from '../store/useAppStore';
import SyncStatusChip from './SyncStatusChip';
import ConflictDialog from './ConflictDialog';

export default function AccountMenu() {
  const auth = useAuth();
  const status = useSyncStatus();
  const navigate = useNavigate();
  const showToast = useAppStore((s) => s.showToast);

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const signedIn = auth.state === 'signed-in';

  async function doAuth(kind: 'in' | 'up') {
    setBusy(true);
    try {
      await (kind === 'in' ? signIn() : signUp());
      setSignInOpen(false);
      if (!isLinked()) {
        setMergeOpen(true);
      } else {
        // Already linked: resume automatically — but resumeSync first verifies
        // this is the SAME account the device was syncing. A different account
        // unlinks (data stays local) and gets the normal first-sync choice.
        const result = await resumeSync();
        if (result === 'mismatch') {
          showToast('You signed in with a different account than this device was syncing.', 'info');
          setMergeOpen(true);
        }
      }
    } catch (e) {
      showToast(`Sign-in failed: ${(e as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function doLink(mode: 'merge' | 'useCloud') {
    setBusy(true);
    try {
      await linkAccount(mode);
      setMergeOpen(false);
      showToast('Cloud sync is on. Your data will stay in sync across devices.', 'success');
    } catch (e) {
      showToast(`Could not start sync: ${(e as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function doSignOut() {
    setAnchor(null);
    // Sign-out does NOT unlink: the device stays linked so signing back in with
    // the same account resumes sync without re-asking the first-sync question.
    // "Stop sync on this device" in Settings is the explicit unlink action.
    await signOut().catch(() => {});
    showToast('Signed out. Your data stays on this device; sync resumes when you sign back in.', 'info');
  }

  if (!signedIn) {
    return (
      <>
        <Button
          size="small"
          startIcon={<LoginIcon />}
          variant="outlined"
          onClick={() => setSignInOpen(true)}
          disabled={auth.state === 'initializing'}
        >
          Sign in
        </Button>

        <Dialog open={signInOpen} onClose={() => setSignInOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Cloud sync &amp; account</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              The app works fully offline — an account is only needed to{' '}
              <strong>sync your prep across devices</strong>. Your data stays on this device until
              you sign in.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setSignInOpen(false)}>Cancel</Button>
            <Button variant="outlined" disabled={busy} onClick={() => doAuth('up')}>
              Create account
            </Button>
            <Button variant="contained" disabled={busy} onClick={() => doAuth('in')}>
              Sign in
            </Button>
          </DialogActions>
        </Dialog>

      </>
    );
  }

  return (
    <>
      <SyncStatusChip onClick={() => (status.state === 'conflicted' ? setConflictOpen(true) : void syncNow())} />
      <Tooltip title={auth.user?.email ?? 'Account'}>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge color="warning" badgeContent={status.conflicts} overlap="circular">
            <AccountCircleIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{auth.user?.name ?? 'Signed in'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {auth.user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void syncNow();
          }}
        >
          <ListItemIcon>
            <SyncIcon fontSize="small" />
          </ListItemIcon>
          Sync now
        </MenuItem>
        {status.conflicts > 0 && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setConflictOpen(true);
            }}
          >
            <ListItemIcon>
              <RuleIcon fontSize="small" />
            </ListItemIcon>
            Resolve conflicts ({status.conflicts})
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={doSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      {/* Rendered in the signed-in state: doAuth flips auth to signed-in before
          opening it, so it must survive the branch switch above. */}
      <FirstSignInDialog open={mergeOpen} busy={busy} onChoose={doLink} onClose={() => setMergeOpen(false)} />
      <ConflictDialog open={conflictOpen} onClose={() => setConflictOpen(false)} />
    </>
  );
}

/** First sign-in on the desktop (offline-first) build: decide how local data
 * and any existing cloud data combine. The hosted web links automatically and
 * never shows this. */
function FirstSignInDialog({
  open,
  busy,
  onChoose,
  onClose,
}: {
  open: boolean;
  busy: boolean;
  onChoose: (mode: 'merge' | 'useCloud') => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set up cloud sync</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ mb: 2 }}>
          Choose how to combine what&apos;s on this device with your cloud account. You can keep
          using the app offline at any time — sync just keeps your devices in step.
        </DialogContentText>
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            startIcon={<MergeTypeIcon />}
            disabled={busy}
            onClick={() => onChoose('merge')}
          >
            Merge this device with the cloud
          </Button>
          <Typography variant="caption" color="text.secondary">
            Uploads everything here and pulls down anything already in your account. Best the first
            time you sign in.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            disabled={busy}
            onClick={() => onChoose('useCloud')}
          >
            Use my cloud data (replace local)
          </Button>
          <Typography variant="caption" color="text.secondary">
            Replaces this device&apos;s prep data with what&apos;s in the cloud. Best on a new device.
            Machine-only settings (Codex path, microphone) are kept.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Not now
        </Button>
      </DialogActions>
    </Dialog>
  );
}
